/**
 * WA0275 · Página pública de respuesta a Propuesta de Subcontratación.
 * URL: /propuesta/:token (sin auth). Botones ACEPTAR / RECHAZAR.
 * Al aceptar, el backend genera y envía automáticamente el contrato para firma.
 */
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, FileText, HardHat } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PropuestaPublicaPage() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [nombre, setNombre] = useState("");
  const [comentario, setComentario] = useState("");
  const [resultado, setResultado] = useState(null);
  const accionSugerida = params.get("accion");

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${BACKEND}/api/propuesta-publica/${token}`);
        setData(r.data);
        if (r.data.subcontratista_nombre) setNombre(r.data.subcontratista_nombre);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Enlace inválido o caducado");
      } finally { setLoading(false); }
    })();
  }, [token]);

  const responder = async (accion) => {
    setEnviando(true);
    try {
      const r = await axios.post(`${BACKEND}/api/propuesta-publica/${token}/responder`, {
        accion, nombre: nombre.trim() || undefined, comentario: comentario.trim() || undefined,
      });
      setResultado(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo registrar la respuesta");
    } finally { setEnviando(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0F1A3C]"><Loader2 className="h-8 w-8 animate-spin text-white" /></div>;
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0F1A3C] text-white p-6">
        <Toaster richColors position="top-center" />
        <div className="text-center">
          <XCircle className="h-10 w-10 mx-auto mb-3 text-red-400" />
          <div className="font-bold">Enlace inválido o propuesta no encontrada</div>
        </div>
      </div>
    );
  }

  const yaRespondida = data.estado !== "enviada" || resultado;
  const estadoFinal = resultado?.estado || data.estado;

  return (
    <div className="min-h-screen bg-[#f4f2ec]" data-testid="propuesta-publica-page">
      <Toaster richColors position="top-center" />
      <div className="bg-[#0F1A3C] text-white px-6 py-4 flex items-center gap-3">
        <HardHat className="h-6 w-6 text-[#E0B053]" />
        <div>
          <div className="font-black text-sm uppercase tracking-widest">Propuesta de Subcontratación</div>
          <div className="text-xs text-white/70">Obra {data.obra_codigo} · {data.partidas_count} partidas · Precio máximo: {Number(data.total_precio_max || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR" })}</div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-4">
        {yaRespondida ? (
          <div className={`rounded-2xl border-2 p-6 text-center ${estadoFinal === "aceptada" ? "border-green-600 bg-green-50" : "border-red-500 bg-red-50"}`}
               data-testid="propuesta-resultado">
            {estadoFinal === "aceptada" ? (
              <>
                <CheckCircle2 className="h-12 w-12 mx-auto mb-2 text-green-600" />
                <div className="text-lg font-black text-green-700">Propuesta ACEPTADA</div>
                <div className="text-sm text-green-800 mt-1">
                  {resultado?.mensaje || "Recibirá el contrato de subcontratación con enlace de firma online por email."}
                </div>
              </>
            ) : (
              <>
                <XCircle className="h-12 w-12 mx-auto mb-2 text-red-500" />
                <div className="text-lg font-black text-red-600">Propuesta RECHAZADA</div>
                <div className="text-sm text-red-700 mt-1">{resultado?.mensaje || "Esta propuesta fue rechazada."}</div>
              </>
            )}
          </div>
        ) : (
          <div className="rounded-2xl border border-[#0F1A3C]/20 bg-white p-5 shadow-sm" data-testid="propuesta-responder-box">
            <div className="text-sm font-black text-[#0F1A3C] mb-2">Responder a la propuesta</div>
            <div className="grid sm:grid-cols-2 gap-2 mb-3">
              <input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Su nombre / empresa"
                     className="border rounded-lg px-3 py-2 text-sm" data-testid="propuesta-nombre-input" />
              <input value={comentario} onChange={(e) => setComentario(e.target.value)} placeholder="Comentario (opcional)"
                     className="border rounded-lg px-3 py-2 text-sm" data-testid="propuesta-comentario-input" />
            </div>
            <div className="flex gap-3 flex-wrap">
              <button onClick={() => responder("aceptar")} disabled={enviando}
                      className={`flex-1 min-w-[180px] py-3 rounded-xl font-black text-white bg-[#0F8A5F] hover:bg-[#0d7a53] disabled:opacity-60 ${accionSugerida === "aceptar" ? "ring-4 ring-[#0F8A5F]/30" : ""}`}
                      data-testid="propuesta-aceptar-btn">
                {enviando ? <Loader2 className="h-4 w-4 animate-spin inline mr-1" /> : "✓"} ACEPTAR PROPUESTA
              </button>
              <button onClick={() => responder("rechazar")} disabled={enviando}
                      className={`flex-1 min-w-[180px] py-3 rounded-xl font-black text-white bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-60 ${accionSugerida === "rechazar" ? "ring-4 ring-red-300" : ""}`}
                      data-testid="propuesta-rechazar-btn">
                ✗ RECHAZAR
              </button>
            </div>
            <div className="text-[11px] text-gray-500 mt-2">
              Al aceptar, recibirá automáticamente por email el contrato de subcontratación con el enlace de firma online.
            </div>
          </div>
        )}

        <div className="rounded-2xl border border-[#0F1A3C]/15 bg-white shadow-sm overflow-hidden">
          <div className="px-4 py-2 bg-[#0F1A3C]/5 flex items-center gap-2 text-xs font-bold text-[#0F1A3C]">
            <FileText className="h-4 w-4" /> Documento de la propuesta
          </div>
          <iframe title="propuesta" srcDoc={data.html} className="w-full border-0" style={{ height: "70vh" }}
                  data-testid="propuesta-html-iframe" />
        </div>
      </div>
    </div>
  );
}
